import { inject, Service } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Api, ApiClient, ApiTypes } from '@app/api';
import { CONSTANTS } from '@shared/constants';
import {
  BrandingForBackend,
  VenueDemographicsConfiguration,
} from '@shared/interfaces/bill-folders';

//TODO: This service naming is outdated, and we need to split it up into multiple other services to contain the functionality in a more logical way. For now, we keep the name to avoid breaking changes, but any new methods should be added to more specific services instead of this one.
@Service()
export class BillFolderBackendService {
  private api = inject(ApiClient);

  /**
   * Several image error handlers call this before any file is set, so a missing uuid yields an empty
   * URL. A size of 0 is left out, because the file route answers 422 to anything outside 1–8192.
   */
  getFileUrl(
    fileUuid: string | null | undefined,
    width?: number,
    height?: number,
    maxWidth?: number,
    maxHeight?: number
  ): string {
    if (!fileUuid) {
      return '';
    }

    return this.api.url(Api.storage.files.byUuid, {
      pathParams: { uuid: fileUuid },
      query: {
        width: width || undefined,
        height: height || undefined,
        maxWidth: maxWidth || undefined,
        maxHeight: maxHeight || undefined,
      },
    });
  }

  /**
   * Uploads a JPEG base64 data URL to file storage and returns the created file UUID.
   * Call this just before saving to avoid orphaned files on cancellation.
   */
  uploadImage$(base64: string): Observable<string> {
    const blob = this.base64ToBlob(base64, 'image/jpeg');
    const formData = new FormData();
    formData.append('file', blob, 'image.jpg');

    return this.api.post(Api.storage.files, { body: formData }).pipe(map(({ uuid }) => uuid));
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const parts = base64.split(',');
    const byteString = atob(parts[1] ?? parts[0]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8 = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      uint8[i] = byteString.charCodeAt(i);
    }
    return new Blob([arrayBuffer], { type: mimeType });
  }

  setFileResolutions(
    ileUuid: string,
    width?: number,
    height?: number,
    maxWidth?: number,
    maxHeight?: number
  ): Observable<Api.storage.files.thumbnail.PostResult> {
    // The thumbnail endpoint takes the requested dimensions as query params, not as a body.
    return this.api.post(Api.storage.files.thumbnail, {
      pathParams: { uuid: ileUuid },
      query: {
        ...(width && { width }),
        ...(height && { height }),
        ...(maxWidth && { maxWidth }),
        ...(maxHeight && { maxHeight }),
      },
    });
  }

  getDemographicsConfiguration$(
    context: string,
    uuid: string
  ): Observable<VenueDemographicsConfiguration> {
    const endpoint =
      context === CONSTANTS.CONTEXT.ACCOUNT
        ? Api.fbi.accounts.demographicsConfiguration
        : Api.fbi.venues.demographicsConfiguration;

    return this.api.get(endpoint, { pathParams: { uuid } });
  }

  updateDemographicsConfiguration$(
    context: string,
    uuid: string,
    questionsObject: VenueDemographicsConfiguration
  ): Observable<ApiTypes.DemographicsConfigurationResponseDto> {
    const endpoint =
      context === CONSTANTS.CONTEXT.ACCOUNT
        ? Api.fbi.accounts.demographicsConfiguration
        : Api.fbi.venues.demographicsConfiguration;

    return this.api.put(endpoint, {
      pathParams: { uuid },
      body: questionsObject,
    });
  }

  updateBranding$(
    context: string,
    uuid: string,
    brandingDetails: BrandingForBackend
  ): Observable<ApiTypes.AccountBrandingResponseDto | ApiTypes.VenueBrandingResponseDto> {
    const endpoint =
      context === CONSTANTS.CONTEXT.ACCOUNT ? Api.fbi.accounts.branding : Api.fbi.venues.branding;

    return this.api.put(endpoint, { pathParams: { uuid }, body: brandingDetails });
  }

  getBrandingInfo$(
    context: string,
    uuid: string
  ): Observable<ApiTypes.AccountBrandingResponseDto | ApiTypes.VenueBrandingResponseDto> {
    const endpoint =
      context === CONSTANTS.CONTEXT.ACCOUNT ? Api.fbi.accounts.branding : Api.fbi.venues.branding;

    return this.api.get(endpoint, { pathParams: { uuid } });
  }
}
